import dayjs from 'dayjs';
import { removeGPSMetadata } from 'src/api/s3/_util/removeGPSMetadata';
import { uploadToS3 } from 'src/api/s3/uploadToS3';
import { postImageUploadRequest, putImageContentType } from 'src/queries/Image';
import { s3BucketName } from 'src/util/env';
import { SelectedImage } from '../index';

export type ImageToUpload = Omit<SelectedImage, 'file'> & { file: File };

export async function uploadImages(
  images: ImageToUpload[],
  companyKey: string
): Promise<{ url: string; key: string }[]> {
  if (images.length === 0) return [];

  const awsCreds = await Promise.all(images.map(() => postImageUploadRequest(companyKey)));
  const blobsWithoutGPSMetadata = await Promise.all(images.map(({ file }) => removeGPSMetadata(file)));

  const uploadRequests = images.map((image, index) => {
    const blobWithoutGPSMetadata = blobsWithoutGPSMetadata[index];
    const extension = image.file.name.split('.').pop();
    const fileName = `${image.key}.${extension ?? 'jpg'}`;

    const now = dayjs().utc();
    const year = now.format('YYYY');
    const month = now.format('MM');
    const day = now.format('DD');

    const form = new FormData();
    Object.keys(awsCreds[index]).forEach((key) => form.append(key, awsCreds[index][key]));
    form.set('key', `videos/${companyKey}/${year}/${month}/${day}/\${filename}`);
    form.set('Content-Type', image.file.type);
    form.append('file', blobWithoutGPSMetadata, fileName);

    const s3Key = `videos/${companyKey}/${year}/${month}/${day}/${fileName}`;
    return {
      response: uploadToS3({
        data: form,
      }),
      s3Key,
      contentType: image.file.type,
      key: image.key,
    };
  });

  await Promise.all(uploadRequests.map(({ response }) => response));
  await Promise.all(uploadRequests.map(({ s3Key, contentType }) => putImageContentType({ s3Key, contentType })));

  return uploadRequests.map(({ s3Key, key }) => ({
    url: `https://${s3BucketName}.s3.amazonaws.com/${s3Key}`,
    key,
  }));
}
